import { Dialog } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { Column, TableInstance } from 'react-table';
import { DataTable, LinearBar } from 'shared/components';
import { useModal, useTitle } from 'shared/hooks';
import { useProducts } from 'shared/queries';
import { Product } from 'shared/types';
import CompetitorColumn from './columns/competitor-column';
import DateColumn from './columns/date-column';
import NumberColumn from './columns/number-column';
import PercentColumn from './columns/percent-column';
import ProductDetailColumn from './columns/product-detail-column';
import RecommendedPriceColumn from './columns/recommended-price-column';
import EditActionButton from './edit-action-button';
import EditPage from './edit-page';
import ExportActionButton from './export-action-button';
import DateFilter from './filters/date-filter';
import SelectFilter from './filters/select-filter';
import ProductSearch from './product-search';
import ShowAllCheckbox from './show-all-checkbox';

export function ProductsPage() {
    useTitle('Belnick Product List');
    const [editOpen, toggleEditPopup] = useModal();
    const { data, isFetching, isLoading, initialTableState, setInitialTableState } = useProducts();

    const columns: Column<Product>[] = useMemo(
        () => [
            {
                Header: 'Status',
                accessor: 'status',
                Filter: SelectFilter,
                width: 100,
            },
            {
                Header: 'Kit Flag',
                accessor: 'kitFlag',
                width: 100,
            },
            {
                Header: 'Product Code',
                accessor: 'productCode',
                disableFilters: true,
            },
            {
                Header: 'Product Name',
                accessor: 'productName',
                disableFilters: true,
                Cell: ProductDetailColumn,
                width: 325,
            },
            {
                Header: 'Category',
                accessor: 'category',
                Filter: SelectFilter,
            },
            {
                Header: 'Product Tier',
                accessor: 'productTier',
                Filter: SelectFilter,
            },
            {
                Header: 'Current Price',
                accessor: 'currentPrice',
                Cell: NumberColumn,
                disableFilters: true,
            },
            {
                Header: 'Competitor Price',
                accessor: 'competitiveSkuPrice',
                disableFilters: true,
                disableSortBy: true,
                Cell: CompetitorColumn,
                width: 160,
            },
            {
                Header: 'Recommended Price',
                accessor: 'targetPrice',
                Cell: NumberColumn,
                disableFilters: true,
            },
            {
                Header: 'Recommendation Reason',
                accessor: 'targetPriceType',
                Filter: SelectFilter,
            },
            {
                Header: 'Final Recommended Price',
                accessor: 'recommendedPrice',
                Cell: RecommendedPriceColumn,
                disableFilters: true,
            },
            {
                Header: 'Final Recommendation Reason',
                accessor: 'reasonForRecommendation',
                Filter: SelectFilter,
            },
            {
                Header: 'Product Margin',
                accessor: 'productMargin',
                Cell: PercentColumn,
                disableFilters: true,
            },
            {
                Header: 'Revised Price',
                accessor: 'revisedPrice',
                Cell: NumberColumn,
                disableFilters: true,
            },
            {
                Header: 'Revised Margin',
                accessor: 'revisedMargin',
                Cell: PercentColumn,
                disableFilters: true,
            },
            {
                Header: 'Price Change %',
                accessor: 'priceChange',
                Cell: PercentColumn,
                disableFilters: true,
            },
            {
                Header: 'Last Approved',
                accessor: 'lastApproved',
                Cell: DateColumn,
                Filter: DateFilter,
            },
            {
                Header: 'Quantity Break',
                accessor: 'quantityBreaksUsed',
                Filter: SelectFilter,
                isVisible: false,
            },
            {
                Header: 'Selling Unit',
                accessor: 'sellingUnitType',
                Filter: SelectFilter,
            },
        ],
        []
    );

    const getRowId = useCallback((row: Product) => row.productCode, []);

    const showAllCheckBox = useCallback((instance: TableInstance<Product>) => {
        return <ShowAllCheckbox instance={instance} />;
    }, []);

    const renderEdit = useCallback(
        (instance: TableInstance<Product>) => (
            <Dialog open={editOpen}>
                <EditPage instance={instance} handleClose={toggleEditPopup} />
            </Dialog>
        ),
        [editOpen, toggleEditPopup]
    );

    const editAction = useCallback(
        (instance: TableInstance<Product>) => (
            <EditActionButton instance={instance} toggleEditPopup={toggleEditPopup} />
        ),
        [toggleEditPopup]
    );

    const exportAction = useCallback(
        (instance: TableInstance<Product>) => <ExportActionButton instance={instance} />,
        []
    );

    const searchAction = useCallback((instance: TableInstance<Product>) => <ProductSearch instance={instance} />, []);

    return (
        <>
            <LinearBar loading={isLoading || isFetching} />
            <DataTable<Product>
                manual={true}
                columns={columns}
                data={data?.products ?? []}
                totalCount={data?.totalCount || 0}
                totalPageCount={data?.totalPages || 0}
                editAction={editAction}
                exportAction={exportAction}
                searchAction={searchAction}
                getRowId={getRowId}
                renderEdit={renderEdit}
                showAllAction={showAllCheckBox}
                initialState={initialTableState}
                saveState={setInitialTableState}
            />
        </>
    );
}
